import type { TrailPoint, BladeKey } from './GameState.ts';
import type { Particle } from './Particle.ts';
import type { RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import type { NativeComboPayload } from 'shared/lib/tv-game/nativeFrame.ts';
import type { KeypointType } from 'shared/lib/tv-game/pose.ts';

export type RemotePhase = 'lobby' | 'countdown' | 'calibration' | 'playing' | 'gameover';

/** Normalized (0–1 game-space) position of a keypoint, pre-computed for rendering. */
export type CalibNormPoint = { nx: number; ny: number };
export type CalibNormMap = Map<KeypointType, CalibNormPoint>;

/** One half after slice when native sends x1,y1,x2,y2 + rotations. */
export interface RemoteFruitHalf {
  nx: number;
  ny: number;
  rotationRad: number;
}

/** Authoritative fruit row from native (normalized coords). */
export interface RemoteFruitRow {
  nx: number;
  ny: number;
  isBomb: boolean;
  isHit: boolean;
  /** Index for juice / fallback PNG. */
  spriteIndex: number;
  /** Resolved `/media/...` path (from `image` + bomb). */
  pngSrc: string;
  /** `*_half.png` for slice / native halves (not used for bombs). */
  halfPngSrc?: string;
  /** Совпадает с нативным `id` (число или строка для Int64). */
  fruitId?: number | string;
  /** Whole-fruit rotation (radians). */
  rotationRad: number;
  /** When set after slice, TV draws halves here instead of local half particles. */
  nativeHalves?: { half1: RemoteFruitHalf; half2: RemoteFruitHalf };
}

export interface RemoteDisplayState {
  phase: RemotePhase;
  fruits: RemoteFruitRow[];
  particles: Particle[];
  bladeTrails: Map<BladeKey, TrailPoint[]>;
  difficulty: number;
  gameStartTime: number;
  /** null = legacy full-canvas mapping; set from native aspectRatio / fruitSize when present. */
  remoteView: RemoteViewLayout | null;
  /** HUD — driven by native frame fields. */
  gameMode: 'CLASSIC' | 'ARCADE' | 'ZEN' | null;
  score: number;
  streak: number;
  /** Session / all-time best from native HUD; null if not sent. */
  bestScore: number | null;
  /** Hearts count — Classic only; null otherwise. */
  lives: number | null;
  /** Seconds remaining — Arcade only; null otherwise. */
  timeLeft: number | null;
  /** Floating combo popup; null = hidden. */
  combo: NativeComboPayload | null;
  /** Player 2 combo popup (split mode only). */
  combo2: NativeComboPayload | null;
  /** Game is paused — TV shows pause overlay. */
  isPaused: boolean;
  /** Game has ended — TV shows game-over overlay with final score. */
  isGameOver: boolean;
  /** Calibration phase: pre-computed normalized keypoint positions for live skeleton. */
  calibNormPoints: CalibNormMap;
  /** Calibration/playing: second player skeleton points for multiplayer payloads. */
  calibNormPointsP2: CalibNormMap;
  /** Whether the player has already sliced the calibration fruit (waiting for server start_game). */
  calibSliced: boolean;
  /** Player 2 score — populated from new per-player JSON format. */
  player2Score: number | null;
  /** Player 2 lives — populated from new per-player JSON format. */
  player2Lives: number | null;
  /** Player 2 streak — populated from new per-player JSON format. */
  player2Streak: number | null;
  /** Fruit Ninja split mode from room `playerCount` (1|2). */
  splitTwoPlayers: boolean;
  /** Per-player game over in split ARCADE: player finished but other still playing. */
  player1GameOver: boolean;
  player2GameOver: boolean;
  /** Blade-trail key prefix for player 1 (e.g. 'slot1' or actual playerId). */
  p1PlayerId: string | null;
  /** Blade-trail key prefix for player 2 in DUO mode; null in solo. */
  p2PlayerId: string | null;
}

export function createRemoteInitialState(): RemoteDisplayState {
  return {
    phase: 'lobby',
    fruits: [],
    particles: [],
    bladeTrails: new Map(),
    difficulty: 1,
    gameStartTime: 0,
    remoteView: null,
    gameMode: null,
    score: 0,
    streak: 0,
    bestScore: null,
    lives: null,
    timeLeft: null,
    combo: null,
    combo2: null,
    isPaused: false,
    isGameOver: false,
    calibNormPoints: new Map(),
    calibNormPointsP2: new Map(),
    calibSliced: false,
    player2Score: null,
    player2Lives: null,
    player2Streak: null,
    splitTwoPlayers: false,
    player1GameOver: false,
    player2GameOver: false,
    p1PlayerId: null,
    p2PlayerId: null,
  };
}
